import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, User, Search, Menu, X, ChevronDown, Wrench, LogOut, Package, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = ['Bosch', 'DeWalt', 'Makita', 'Milwaukee', 'Hikoki', 'Stanley', 'Black+Decker', 'Total', 'Ingco'];
const CATEGORIES = ['Drills & Drivers', 'Angle Grinders', 'Rotary Hammers', 'Circular Saws', 'Jig Saws', 'Sanders', 'Impact Wrenches', 'Heat Guns', 'Pressure Washers', 'Batteries & Chargers', 'Accessories'];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopDropdown, setShopDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);

  const searchRef = useRef(null);
  const shopRef = useRef(null);
  const userRef = useRef(null);
  const suggestTimer = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
      if (shopRef.current && !shopRef.current.contains(e.target)) setShopDropdown(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(suggestTimer.current);
    if (val.length >= 2) {
      suggestTimer.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/products/search/suggestions?q=${encodeURIComponent(val)}`);
          setSuggestions(res.data);
          setShowSuggestions(true);
        } catch { setSuggestions([]); }
      }, 250);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (s) => {
    navigate(`/products/${s.id}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 header-bg border-b border-white/5">
      {/* Top bar */}
      <div className="bg-primary/10 border-b border-primary/20 py-1.5 px-4 text-center text-xs font-body text-primary/90 tracking-wide">
        FREE DELIVERY on orders over AED 200 &nbsp;|&nbsp; Genuine Products &nbsp;|&nbsp; UAE Warranty Support
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center gap-4 h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 group" data-testid="header-logo">
            <div className="w-8 h-8 bg-primary flex items-center justify-center transition-transform group-hover:scale-105">
              <Wrench size={16} className="text-black" strokeWidth={2.5} />
            </div>
            <div className="hidden sm:block">
              <div className="font-heading font-black text-base uppercase tracking-tighter text-white leading-none">M N M TOOLS</div>
              <div className="text-primary text-xs font-body font-bold tracking-widest uppercase leading-none">Tools Hub</div>
            </div>
          </Link>

          {/* Shop dropdown */}
          <div className="hidden md:block relative" ref={shopRef}>
            <button
              className="flex items-center gap-1 text-white/80 hover:text-primary transition-colors text-sm font-body font-semibold uppercase tracking-wide py-1"
              onClick={() => setShopDropdown(!shopDropdown)}
              data-testid="shop-dropdown-btn"
            >
              Shop <ChevronDown size={14} className={`transition-transform ${shopDropdown ? 'rotate-180' : ''}`} />
            </button>
            {shopDropdown && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-surface border border-white/10 shadow-2xl z-50 animate-fade-in">
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Categories</p>
                    <div className="grid grid-cols-1 gap-0.5">
                      {CATEGORIES.slice(0, 8).map(cat => (
                        <Link
                          key={cat}
                          to={`/products?category=${encodeURIComponent(cat)}`}
                          className="text-sm text-white/70 hover:text-primary hover:bg-white/5 px-2 py-1.5 transition-colors"
                          onClick={() => setShopDropdown(false)}
                          data-testid={`category-link-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {cat}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Brands</p>
                    <div className="flex flex-wrap gap-1">
                      {BRANDS.map(brand => (
                        <Link
                          key={brand}
                          to={`/products?brand=${encodeURIComponent(brand)}`}
                          className="text-xs bg-white/5 hover:bg-primary/20 hover:text-primary px-2 py-1 transition-colors"
                          onClick={() => setShopDropdown(false)}
                        >
                          {brand}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search bar */}
          <div className="flex-1 max-w-xl relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search tools, brands, models..."
                className="w-full bg-white/5 border border-white/10 focus:border-primary/60 focus:bg-white/8 text-white placeholder-white/30 text-sm px-4 pr-10 py-2.5 outline-none transition-all"
                style={{ borderRadius: 0 }}
                data-testid="header-search-input"
              />
              <button type="submit" className="absolute right-0 top-0 bottom-0 px-3 text-white/40 hover:text-primary transition-colors" data-testid="header-search-btn">
                <Search size={16} />
              </button>
            </form>
            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-surface border border-white/10 shadow-2xl z-50 max-h-72 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                    onClick={() => handleSuggestionClick(s)}
                    data-testid={`suggestion-item-${i}`}
                  >
                    {s.images?.[0] && <img src={s.images[0]} alt="" className="w-8 h-8 object-cover flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.brand} &middot; {s.category}</p>
                    </div>
                    <span className="ml-auto text-primary text-sm font-mono font-bold">AED {s.price}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-1">
            {/* Wishlist */}
            <Link
              to={user ? "/dashboard?tab=wishlist" : "/auth"}
              className="hidden sm:flex items-center justify-center w-9 h-9 text-white/60 hover:text-primary transition-colors"
              data-testid="header-wishlist-btn"
            >
              <Heart size={18} />
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative flex items-center justify-center w-9 h-9 text-white/60 hover:text-primary transition-colors"
              data-testid="header-cart-btn"
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary text-black text-xs font-black w-4 h-4 flex items-center justify-center leading-none" data-testid="cart-count-badge">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>

            {/* User */}
            {user ? (
              <div className="relative" ref={userRef}>
                <button
                  className="flex items-center gap-1.5 text-white/60 hover:text-primary transition-colors px-1 py-1"
                  onClick={() => setUserDropdown(!userDropdown)}
                  data-testid="header-user-btn"
                >
                  <div className="w-7 h-7 bg-primary/20 border border-primary/40 flex items-center justify-center">
                    <span className="text-primary text-xs font-bold uppercase">{user.name?.[0]}</span>
                  </div>
                  <span className="hidden md:block text-sm font-body font-medium truncate max-w-20">{user.name?.split(' ')[0]}</span>
                </button>
                {userDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-surface border border-white/10 shadow-2xl z-50 animate-fade-in">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-sm font-bold text-white">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:text-primary hover:bg-white/5 transition-colors" onClick={() => setUserDropdown(false)}>
                      <Package size={14} /> My Orders
                    </Link>
                    <Link to="/dashboard?tab=wishlist" className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:text-primary hover:bg-white/5 transition-colors" onClick={() => setUserDropdown(false)}>
                      <Heart size={14} /> Wishlist
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors border-t border-white/10" onClick={() => setUserDropdown(false)}>
                        <Settings size={14} /> Admin Panel
                      </Link>
                    )}
                    <button onClick={() => { logout(); setUserDropdown(false); navigate('/'); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors border-t border-white/10" data-testid="logout-btn">
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className="hidden sm:flex items-center gap-1.5 bg-primary text-black text-xs font-bold uppercase tracking-wider px-4 h-9 hover:bg-white transition-colors"
                data-testid="header-login-btn"
              >
                <User size={14} /> Login
              </Link>
            )}

            {/* Mobile menu toggle */}
            <button
              className="md:hidden flex items-center justify-center w-9 h-9 text-white/60 hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-surface border-t border-white/10 animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-3">
            <form onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search tools..."
                className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm px-4 pr-10 py-2.5 outline-none"
                style={{ borderRadius: 0 }}
              />
              <button type="submit" className="absolute right-0 top-0 bottom-0 px-3 text-white/40"><Search size={16} /></button>
            </form>
            <div className="border-t border-white/10 pt-3">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {CATEGORIES.slice(0, 6).map(cat => (
                  <Link key={cat} to={`/products?category=${encodeURIComponent(cat)}`}
                    className="text-sm text-white/70 hover:text-primary py-1 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}>
                    {cat}
                  </Link>
                ))}
              </div>
            </div>
            {!user && (
              <Link to="/auth" className="block text-center bg-primary text-black text-sm font-bold uppercase py-2.5 tracking-wider" onClick={() => setMobileMenuOpen(false)}>
                Login / Register
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
